import { Spine } from "@esotericsoftware/spine-pixi-v7";
import * as PIXI from "pixi.js";
import {
  setShowAdditionalFreeSpins,
  setCriticalAnimationPlaying,
} from "../../../../State/actions";
import { Settings } from "../../../../settings";
import { PixiSettings } from "../../../../pixiSettings";
import TickerTimeout from "../../../../utils/TickerTimeout";
import SpineAtlasUploader from "../../../../utils/SpineAtlasUploader";
import { FONT_STYLES, scaleStyle } from "../../../../fontStyles";
import { soundManager } from "../../../../SoundManager";
import { SOUND_NAME } from "../../../../sounds";

// const SPINE_NAME = "additional_free_spin_dialog";
// The animator's new popup (2026-09-30), shared with the FS intro / reload and
// the Hold & Win intro — only the number and the wording differ. GDD
// "Retrigger": present "+3 FREE SPINS" / "+5 FREE SPINS". Here:
//   YOU WON / +n (in the eye) / FREE SPINS
// played in -> idle (RETRIGGER_IDLE_LOOPS) -> out, closing itself — no tap, as
// before. The new spine has no box clips and no events, so the beats run on
// the clips' own lengths (retriggerTimeline).
const SPINE_NAME = "hold_and_win_intro";
const RETRIGGER_IDLE_LOOPS = 1;
const TOP_SLOTS = ["txt_slot_you_have"];
const NUMBER_SLOT = "txt_slot_spins_respins_amount";
// The Hold & Win / FS intro sizes, value for value (same art).
const RETRIGGER_LINE_FONT_SIZE = 140;
const RETRIGGER_NUMBER_FONT_SIZE = 240;
const RETRIGGER_LINE_MAX_WIDTH = 1100;

// ============================================================================
// Sizing — the WinAnimationSecondary baseline, value for value, exactly as the
// free-spin intro takes it. All 1.0: natural size until proven otherwise.
// `offset` is a multiple of symbolHeight, pushed UP from the screen centre.
// ============================================================================
// `buttonYOffset` lifts the tap word, in SPINE UNITS. Negative is up.
//
// Every popup puts the word at the SAME screen position, so opening two at
// once shows them perfectly aligned. Total win sets that position: its bone
// sits at -480.24 and lifts 43.66, landing on spine y -436.58 — the offset
// that gives it the same gap from the value that the xbet has.
//
// This spine's tap bone is at -471.38, so it needs -34.80 to reach the same line.
// const DEVICE_MULTIPLIERS_MOBILE_SMALL_PORTRAIT = { scale: 1.0, offset: 0.0, buttonYOffset: 0.0 };
const DEVICE_MULTIPLIERS_MOBILE_SMALL_PORTRAIT = { scale: 0.9, offset: 0.0, buttonYOffset: 0.0 }; // 0.9 everywhere to start — the new art is big (2026-09-30)
// const DEVICE_MULTIPLIERS_MOBILE_SMALL_LANDSCAPE = { scale: 1.0, offset: 0.0, buttonYOffset: -34.8 };
const DEVICE_MULTIPLIERS_MOBILE_SMALL_LANDSCAPE = { scale: 0.9, offset: 0.0, buttonYOffset: -34.8 }; // 0.9 everywhere to start — the new art is big (2026-09-30)
// const DEVICE_MULTIPLIERS_MOBILE_PORTRAIT = { scale: 1.0, offset: 0.0, buttonYOffset: 0.0 };
const DEVICE_MULTIPLIERS_MOBILE_PORTRAIT = { scale: 0.9, offset: 0.0, buttonYOffset: 0.0 }; // 0.9 everywhere to start — the new art is big (2026-09-30)
// Natural size runs too large on a standard landscape phone.
// const DEVICE_MULTIPLIERS_MOBILE_LANDSCAPE = { scale: 0.9, offset: 0.0, buttonYOffset: -34.8 };
const DEVICE_MULTIPLIERS_MOBILE_LANDSCAPE = { scale: 0.9, offset: 0.0, buttonYOffset: -34.8 }; // 0.9 everywhere to start — the new art is big (2026-09-30)
// const DEVICE_MULTIPLIERS_TABLET_PORTRAIT = { scale: 1.0, offset: 0.0, buttonYOffset: 0.0 };
const DEVICE_MULTIPLIERS_TABLET_PORTRAIT = { scale: 0.9, offset: 0.0, buttonYOffset: 0.0 }; // 0.9 everywhere to start — the new art is big (2026-09-30)
// const DEVICE_MULTIPLIERS_TABLET_LANDSCAPE = { scale: 1.0, offset: 0.0, buttonYOffset: 0.0 };
const DEVICE_MULTIPLIERS_TABLET_LANDSCAPE = { scale: 0.9, offset: 0.0, buttonYOffset: 0.0 }; // 0.9 everywhere to start — the new art is big (2026-09-30)
// Desktop takes the landscape-phone treatment: same trim, same tap-word lift.
// const DEVICE_MULTIPLIERS_DESKTOP = { scale: 0.9, offset: 0.0, buttonYOffset: -34.8 };
const DEVICE_MULTIPLIERS_DESKTOP = { scale: 0.9, offset: 0.0, buttonYOffset: -34.8 }; // 0.9 everywhere to start — the new art is big (2026-09-30)

// ── One clip, played once ──
//
// Unlike the intro, this spine carries NO skins. The case is chosen by
// ANIMATION, and each one is a single 2.8333s one-shot with the entry, the
// hold and the exit joined together:
//
//   lvl_x3            the x3 box shuts
//   lvl_x5            the x5 box shuts
//   lvl_x10           the x10 box shuts — the LAST one
//   additional_spins  nothing shuts. All four boxes are already closed and
//                     the spins came from the feature's own +FS cells.
//
// The export used to carry an in / idle / out triple per level in a folder,
// and this controller drove the three by hand. It no longer does: the popup
// plays its clip, announces on the way through, and closes when the clip
// ends. Nothing here waits for a tap, so nothing here needs to hold.
// ── TWO clips per rung, and which one plays is the whole feature ──
//
// The card carries four boxes — x2, x3, x5, x10 — and `fs_state.level` is
// 1-indexed over five rungs, so the multiplier ladder is x1 x2 x3 x5 x10 and
// level 1 (x1) has NO box (docs/api-contract-mad-labs-freespins.md).
//
//   lvl_x{m}           SHUTS the box for multiplier m. Played only when a box
//                      really closed this round.
//   additional_spins   the plain card — spins and text, no box, no rung.
//                      Played whenever spins arrive with no box closing.
//
// The rig also ships additional_spins_x2/x3/x5/x10, a lit card per rung. They
// are NOT used: spins that close no box are not about the rung. See
// NO_BOX_CLIP.
//
// The old rig had ONE all-closed clip and it was used only at the top rung, so
// every other level still shut its box a second time on a feature retrigger.
const LEVEL_MULTIPLIERS = [1, 2, 3, 5, 10];

/**
 * The card that shuts a box — one per rung, and the rig now ships all four.
 *
 * `lvl_x2` was missing from the 2026-09-18 export, which is why a fallback
 * chain grew here. The animator delivered it; the chain is gone with it.
 * Verified from the export by the door bones each clip moves, not by its name:
 * lvl_x2 → mystery_door_l/r, x3 → l2/r2, x5 → l3/r3, x10 → l4/r4.
 */
const closeClip = (multiplier) => `lvl_x${multiplier}`;

/**
 * The card for spins that closed NO box.
 *
 * ── One clip, not one per rung ──
 *
 * Free spins arrive two ways. Crossing a rung shuts a box, and that is a
 * different picture per rung, so it needs `lvl_x{m}`. A bonus symbol paying
 * +2 or +3 with the box still filling is not about the rung at all — it is
 * just "here are some more spins", and it looks the same whatever level the
 * room is on.
 *
 * So the per-rung glow cards — `additional_spins_x2/x3/x5/x10` — say something
 * this case does not need to say, and they are no longer asked for. Vladimir,
 * 2026-09-21. They stay in the rig; nothing here plays them.
 */
const NO_BOX_CLIP = "additional_spins";

/**
 * The rung assumed when the caller names one the ladder does not have.
 *
 * x3 is the middle of [1,2,3,5,10]: wrong in the smallest way either
 * direction, and it is only reached by a bad argument to setLevel.
 */
const DEFAULT_MULTIPLIER = 3;

// The label slot. `txt_slot_freespipns` is a TYPO in the export — the bone
// beneath it is spelled correctly. Both spellings are listed so a corrected
// re-export keeps working without a code change.
// const LABEL_SLOTS = ["txt_slot_freespipns", "txt_slot_freespins"];
const LABEL_SLOTS = ["txt_slot_spins_respins", "txt_slot_freespipns", "txt_slot_freespins"];

const LABEL_FONT_SIZE = 84;

/**
 * The three beats inside the clip, as spine events.
 *
 *   box_shut    the winning box pops and the count is shown
 *   in_end      the card is fully assembled and the label is up — the screen
 *               is covered, so this is the moment to change anything behind
 *               it (the room colour, the spin counter)
 *   out_start   everything begins to fade
 *
 * ── These are keyed BY HAND and a re-export will destroy them ──
 *
 * The rig declares `in_start` / `in_end` / `idle_start` / `idle_end` /
 * `out_start` / `out_end` and the animator keys none of them; `box_shut` is
 * ours. They were added to the JSON directly, so the next export from Spine
 * drops all three unless the animator keys them in the project.
 *
 * That is why FALLBACK_BEATS exists below. If an event never arrives the
 * fraction fires instead, so a fresh export degrades to the old timing rather
 * than to a popup that never announces and never closes.
 */
const EVENT_BOX_SHUT = "box_shut";
const EVENT_IN_END = "in_end";
const EVENT_OUT_START = "out_start";

/**
 * Where each beat sits, as a FRACTION of the clip, for when the event is gone.
 *
 * Measured off the clip: 0.9 / 2.8333, 1.6667 / 2.8333, 2.5667 / 2.8333. A
 * fraction and not a number of milliseconds so a clip of a different length
 * carries them with it.
 */
const FALLBACK_BEATS = {
  boxShut: 0.3176,
  inEnd: 0.5882,
  outStart: 0.9059,
};

/**
 * AdditionalFreeSpinsController — "you won more free spins" (GDD §9).
 *
 * Shown MID-FEATURE, when a retrigger awards additional spins on top of the
 * ones already running. Was FreeSpinRetriggerController, which played a single
 * clip and closed itself; this waits for the player, like every other popup.
 */
class AdditionalFreeSpinsController {
  constructor(container, dimensionsContext, dispatch, symbolHeight, overlayRef, t) {
    this.container = container;
    this.dimensionsContext = dimensionsContext;
    this.dispatch = dispatch;
    this.symbolHeight = symbolHeight;
    this.overlayRef = overlayRef;
    this.t = t;

    this.dimensions = dimensionsContext?.dimensions;

    this.spine = null;
    this.isPlaying = false;
    this.initialized = false;

    // Fallbacks only. Each one is armed at startSequence and cleared by the
    // spine event it stands in for, so whichever arrives first wins and the
    // other never runs. See FALLBACK_BEATS.
    this.inWinTimeout = null;
    this.inIdleTimeout = null;
    this.outStartTimeout = null;
    this.completeTimeout = null;
    this._showRAF = null;

    this.labelText = null;

    // The package won. The multiplier picks the clip; count fills the label.
    this.level = DEFAULT_MULTIPLIER;
    this._noBoxClosed = false;
    this._spinCount = 0;

    // One beat may only fire once per play, whether it came from the event or
    // from its fallback timer.
    this._fired = { boxShut: false, inEnd: false, outStart: false, done: false };

    this._configCache = null;
    this._lastDevice = null;
    this._lastOrientation = null;
    this._availableAnimationsCache = null;

    this.initializeSpine();
  }

  // ── Sizing ───────────────────────────────────────────────────────────────

  /** Scale and offset for the current device and orientation, cached on both. */
  getDeviceMultipliers() {
    const { device, orientation, isMobileSmall } = this.dimensionsContext || {};

    if (
      this._configCache !== null &&
      this._lastDevice === device &&
      this._lastOrientation === orientation &&
      this._lastIsMobileSmall === isMobileSmall
    ) {
      return this._configCache;
    }

    const isPortrait = orientation === PixiSettings.ORIENTATION.PORTRAIT;
    let multipliers;
    if (device === PixiSettings.DEVICE.MOBILE) {
      if (isMobileSmall) {
        multipliers = isPortrait
          ? DEVICE_MULTIPLIERS_MOBILE_SMALL_PORTRAIT
          : DEVICE_MULTIPLIERS_MOBILE_SMALL_LANDSCAPE;
      } else {
        multipliers = isPortrait
          ? DEVICE_MULTIPLIERS_MOBILE_PORTRAIT
          : DEVICE_MULTIPLIERS_MOBILE_LANDSCAPE;
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      multipliers =
        orientation === PixiSettings.ORIENTATION.PORTRAIT
          ? DEVICE_MULTIPLIERS_TABLET_PORTRAIT
          : DEVICE_MULTIPLIERS_TABLET_LANDSCAPE;
    } else {
      multipliers = DEVICE_MULTIPLIERS_DESKTOP;
    }

    this._configCache = multipliers;
    this._lastDevice = device;
    this._lastOrientation = orientation;
    this._lastIsMobileSmall = isMobileSmall;
    return multipliers;
  }

  applyScaleAndCenter() {
    if (!this.spine || !this.dimensions) return;

    const nativeSize = Settings.SYMBOL_HEIGHT;
    const targetSize = this.symbolHeight || nativeSize;
    let scale = Math.max(0.1, Math.min(targetSize / nativeSize, 2));

    const { scale: deviceScaleMultiplier, offset: deviceOffsetMultiplier } =
      this.getDeviceMultipliers();
    scale *= deviceScaleMultiplier;

    this.spine.scale.set(scale);

    // The parent container sits at the screen centre, so 0,0 IS dead centre.
    this.spine.x = 0;
    this.spine.y = -(this.symbolHeight || 0) * deviceOffsetMultiplier;

  }

  updateDimensions(dimensionsContext, symbolHeight) {
    const oldDims = this.dimensionsContext?.dimensions;
    const newDims = dimensionsContext?.dimensions;

    const changed =
      !oldDims ||
      oldDims.width !== newDims?.width ||
      oldDims.height !== newDims?.height ||
      this.dimensionsContext?.device !== dimensionsContext?.device ||
      this.dimensionsContext?.orientation !== dimensionsContext?.orientation ||
      this.symbolHeight !== symbolHeight;

    if (!changed) return;

    this.dimensionsContext = dimensionsContext;
    this.dimensions = dimensionsContext?.dimensions;
    this.symbolHeight = symbolHeight;
    this._configCache = null;

    this.applyScaleAndCenter();
  }

  // ── Setup ────────────────────────────────────────────────────────────────

  initializeSpine() {
    try {
      this.spine = this.createSpine();
      if (!this.spine?.skeleton || !this.spine?.state) return;

      this.container.addChild(this.spine);
      // Hidden by ALPHA, never by unmounting — that is what gives `out` a
      // chance to play.
      // ── Hidden at alpha 0, not 0.001 ──
      //
      // 0.001 still RENDERS: PIXI skips a subtree only at `worldAlpha <= 0`. The
      // near-zero was there to keep PIXI's texture GC from unloading this spine's
      // atlas after 60 idle seconds — and that sweep is now off (see Stage.js), so
      // the trick has nothing left to buy. At 0 the whole subtree is skipped, and
      // any fade that raises alpha still brings it back exactly as before.
      this.spine.alpha = 0;
      if (this.spine) this.spine.autoUpdate = false;
      this.spine.zIndex = 20;

      this.applyScaleAndCenter();
      this.initialized = true;
      this._availableAnimationsCache = null;

      this.warmUpSpine();
      this.initializeLabel();
      // NO tap-to-continue. This popup closes itself the moment its entry clip
      // ends and accepts no input at all — see the note above. The spine
      // still keys `txt_slot_tap_to_continue`, but it is an empty marker slot,
      // so leaving it unattached draws nothing.
      //
      // initializeButton / _buttonLabel / updateButtonText and the shared
      // attachTapToContinue import are gone with it: a popup that must never
      // be clickable should not carry the machinery to become clickable.

    } catch (error) {
      console.warn("[AdditionalFreeSpins] Init failed:", error);
    }
  }

  createSpine() {
    const assets = PIXI.Assets.cache;
    const skeletonAlias = `${SPINE_NAME}-skeleton`;
    const atlasAlias = `${SPINE_NAME}-atlas`;
    if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) {
      console.warn("[AdditionalFreeSpins] Spine not loaded:", SPINE_NAME);
      return null;
    }
    return Spine.from({ skeleton: skeletonAlias, atlas: atlasAlias });
  }

  /** Pose one frame so the atlas reaches the GPU before the first show. */
  warmUpSpine() {
    if (!this.spine?.state) return;
    try {
      const name = this._clipName();
      if (name) {
        if (this.spine) this.spine.autoUpdate = true;
        this.spine.state.setAnimation(0, name, false);
        this.spine.state.update(0.016);
        // Spine 4.2 REQUIRES an argument here.
        if (this.spine.skeleton.physics !== undefined) {
          this.spine.skeleton.updateWorldTransform(2);
        } else {
          this.spine.skeleton.updateWorldTransform(0);
        }
        this.spine.state.clearTracks();
      }
      this.spine.alpha = 0;
      if (this.spine) this.spine.autoUpdate = false;
      SpineAtlasUploader.upload(SPINE_NAME);
    } catch {
      // Optimisation only.
    }
  }

  getAvailableAnimations() {
    if (!this.spine?.skeleton?.data?.animations) return [];
    if (this._availableAnimationsCache !== null) {
      return this._availableAnimationsCache;
    }
    this._availableAnimationsCache = this.spine.skeleton.data.animations.map(
      (a) => a.name,
    );
    return this._availableAnimationsCache;
  }

  /**
   * The one clip this play uses.
   *
   * Two cases and no others: a box closed, or it did not.
   *
   *   box closed      lvl_x{multiplier}   the rung's own card, doors and all
   *   no box closed   additional_spins    the plain card, spins and text
   *
   * The long fallback chain that used to live here — closer falls back to the
   * matching glow, glow falls back to the next rung up — existed only because
   * the rig shipped no `lvl_x2` and no card for x1. Both are in the export
   * now, so every branch of it was unreachable, and a fallback nobody can
   * reach is a lie about what the rig contains.
   *
   * Returns null only when the rig has neither clip, and startSequence releases
   * its caller on a null rather than hanging the round.
   */
  _clipName() {
    const available = this.getAvailableAnimations();
    // The new spine: one card for every retrigger, entered with `in`.
    if (available.includes("in")) return "in";
    const wanted = this._noBoxClosed
      ? NO_BOX_CLIP
      : closeClip(this.level);
    if (available.includes(wanted)) return wanted;

    // A rig that does not ship what the round asked for. Say so — silence here
    // is a popup that announces nothing and a round that looks stalled.
    console.warn(
      `[AdditionalFreeSpins] No "${wanted}" clip in the rig. Falling back to "${NO_BOX_CLIP}".`,
    );
    return available.includes(NO_BOX_CLIP) ? NO_BOX_CLIP : null;
  }

  _resolveSlot(candidates) {
    const skeleton = this.spine?.skeleton;
    if (!skeleton) return null;
    for (const name of candidates || []) {
      if (skeleton.findSlot(name)) return name;
    }
    return null;
  }

  // ── Content ──────────────────────────────────────────────────────────────

  /**
   * The MULTIPLIER this card is about — 1, 2, 3, 5 or 10.
   *
   * Which one it is depends on what happened: the multiplier of the level just
   * REACHED when a box closed, the multiplier the room is already ON when none
   * did. The caller decides that; this only picks the artwork.
   */
  setLevel(multiplier) {
    this.level = LEVEL_MULTIPLIERS.includes(multiplier)
      ? multiplier
      : DEFAULT_MULTIPLIER;
  }

  /**
   * "More spins, but NO box closed this round."
   *
   * Every `lvl_x*` clip ANIMATES its box shutting, so playing one when nothing
   * moved shows the player a box closing that closed long ago. True at every
   * rung, not only the top one: a Golden Mystery +FS cell can add spins at any
   * level without touching the ladder.
   *
   * The glow clips are the same card with the boxes already lit and both doors
   * of the current one already shut on frame 0 — nothing moves but the glow,
   * the burst and the count.
   *
   * Set on every play, not only when true — the controller lives for the
   * session, so a flag left raised would take the next real level-up with it.
   *
   * @param {boolean} noBoxClosed
   */
  setNoBoxClosed(noBoxClosed) {
    this._noBoxClosed = !!noBoxClosed;
  }

  /** How many spins were added. Rendered as "+N FREE SPINS". */
  setSpinCount(count) {
    this._spinCount = count;
    // if (this.labelText) this.labelText.text = this._labelString();
    const lines = this._retriggerLines();
    if (this.topText) this.topText.text = lines.top;
    if (this.numberText) this.numberText.text = lines.number;
    if (this.labelText) this.labelText.text = lines.bottom;
    [this.topText, this.numberText, this.labelText].forEach((t) => this._fitText(t));
  }

  /**
   * YOU WON / +n / FREE SPIN(S) — one sentence, split over the three places
   * the art has (the intro's pattern). Line 1 is the number, drawn in the eye.
   */
  _retriggerLines() {
    const count = this._spinCount ?? 0;
    const sentence = this.t("popup_additional_free_spins", {
      count,
      defaultValue:
        count === 1 ? "You won\n+1\nFree Spin" : `You won\n+${count}\nFree Spins`,
    });
    const lines = String(sentence).split("\n");
    return {
      top: (lines[0] ?? "").toUpperCase(),
      number: `+${count}`,
      bottom: (lines[lines.length - 1] ?? "").toUpperCase(),
    };
  }

  /** Fit on the TEXT — spine-pixi writes the bone's scale onto the container. */
  _fitText(textObj) {
    if (!textObj) return;
    textObj.scale.set(1);
    if (textObj.width > RETRIGGER_LINE_MAX_WIDTH) {
      textObj.scale.set(RETRIGGER_LINE_MAX_WIDTH / textObj.width);
    }
  }

  /** One text in one slot (the three lines of the new spine). */
  _addRetriggerText(slotName, text, fontSize) {
    const slot = slotName ? this.spine?.skeleton?.findSlot(slotName) : null;
    if (!slot) return null;
    const container = new PIXI.Container();
    container.name = `${slotName}_text_attached_to_slot`;
    const style = fontSize === RETRIGGER_NUMBER_FONT_SIZE
      ? FONT_STYLES.gambleLevel?.high || FONT_STYLES.winCelebrationCounter
      : FONT_STYLES.freeSpinsText;
    const textObj = new PIXI.Text(text, new PIXI.TextStyle(scaleStyle(style, fontSize)));
    textObj.resolution = 2;
    textObj.anchor.set(0.5);
    container.addChild(textObj);
    this._fitText(textObj);
    this.spine.addSlotObject(slotName, container);
    return textObj;
  }

  /**
   * "+N FREE SPINS", with the WORD following the number.
   *
   * The art is one line, so the shared four-line sentences do not fit here —
   * this plate wants the words on their own. That is exactly what the game
   * rules author for it: `{count, plural, one {Free spin} other {Free spins}}`.
   *
   * It has its OWN id — `popup_additional_free_spins_label` — rather than
   * borrowing the gamble card's `gamble_free_spins_count`, which carries the
   * same English today. The copy owner asked for the split, 2026-09-21: two
   * surfaces, two ids, so a translator can word them differently without one
   * change landing on both.
   *
   * It used to take `free_spins_left`, a fixed word with no plural at all, so
   * a +1 read "+1 FREE SPINS". Fixed 2026-09-21.
   *
   * Uppercased here rather than in the string: the art is caps and the copy is
   * authored in sentence case.
   */
  _labelString() {
    const words = this.t("popup_additional_free_spins_label", {
      count: this._spinCount ?? 0,
    }).toUpperCase();
    return `+${this._spinCount} ${words}`;
  }

  /**
   * The "+N FREE SPINS" line, in the spine's own slot.
   *
   * Same style and size as the free-spin intro's label, so the two popups read
   * as one family.
   */
  initializeLabel() {
    // ── The new spine: three lines (2026-09-30) ──
    if (this.spine?.skeleton?.findSlot(NUMBER_SLOT)) {
      const lines = this._retriggerLines();
      this.topText = this._addRetriggerText(
        this._resolveSlot(TOP_SLOTS),
        lines.top,
        RETRIGGER_LINE_FONT_SIZE,
      );
      this.numberText = this._addRetriggerText(
        NUMBER_SLOT,
        lines.number,
        RETRIGGER_NUMBER_FONT_SIZE,
      );
      this.labelText = this._addRetriggerText(
        this._resolveSlot(LABEL_SLOTS),
        lines.bottom,
        RETRIGGER_LINE_FONT_SIZE,
      );
      this.labelContainer = this.labelText?.parent ?? null;
      return;
    }
    const slotName = this._resolveSlot(LABEL_SLOTS);
    if (!slotName) {
      console.warn("[AdditionalFreeSpins] No label slot:", LABEL_SLOTS);
      return;
    }

    const container = new PIXI.Container();
    container.name = "additional_free_spins_label_attached_to_slot";

    this.labelText = new PIXI.Text(
      this._labelString(),
      new PIXI.TextStyle(
        scaleStyle(FONT_STYLES.freeSpinsText, LABEL_FONT_SIZE),
      ),
    );
    this.labelText.resolution = 2;
    this.labelText.anchor.set(0.5);
    container.addChild(this.labelText);

    // The bone's length is the artist's statement of how much room there is.
    const boneWidth =
      this.spine.skeleton.findSlot(slotName)?.bone?.data?.length || 0;
    if (boneWidth > 0 && this.labelText.width > boneWidth) {
      container.scale.set(boneWidth / this.labelText.width);
    }

    this.spine.addSlotObject(slotName, container);
    this.labelContainer = container;
  }

  getSpineAnimationDuration(animationName) {
    const anim = this.spine?.skeleton?.data?.animations?.find(
      (a) => a.name === animationName,
    );
    return anim?.duration ? Math.round(anim.duration * 1000) : 1000;
  }

  _clearTimeouts() {
    for (const key of [
      "inWinTimeout",
      "inIdleTimeout",
      "outStartTimeout",
      "completeTimeout",
    ]) {
      if (this[key]) {
        this[key].clear();
        this[key] = null;
      }
    }
  }

  // ── Sequence ─────────────────────────────────────────────────────────────

  /**
   * Open it: fade the overlay in, play the clip once, close on its last frame.
   *
   * @param {object}   [hooks]
   * @param {Function} [hooks.onIdle]   Fired at `in_end` — the card is fully
   *   assembled and the screen is covered, so this is the moment to change
   *   anything behind it.
   * @param {Function} [hooks.onClosed] Fired after the clip has finished and
   *   the popup is gone. The level-up sequence resumes here.
   */
  startSequence(hooks = {}) {
    // ── A bail must still release the caller ──
    //
    // The level-charge sequence AWAITS onClosed and holds
    // criticalAnimationPlaying while it waits. Returning here without calling
    // it strands the round outright: the popup never opens, the promise never
    // settles, and the screen stays locked with no way forward. Every exit
    // from this method has to end the caller's wait, whether or not a popup
    // was actually shown.
    if (this.isPlaying) {
      // A popup is genuinely on screen. It owns the flag and will clear it on
      // its own close, so only the new caller's wait is ended here — clearing
      // the flag would pull the dim out from under a live popup.
      console.warn("[AdditionalFreeSpins] startSequence refused — already playing");
      hooks.onClosed?.();
      return;
    }
    if (!this.spine?.state) {
      // Nothing will ever run. The flag is up with no popup behind it, which
      // is the locked-screen case — see _releaseCaller.
      console.warn("[AdditionalFreeSpins] startSequence refused — no spine");
      this._onIdle = null;
      this._onClosed = hooks.onClosed ?? null;
      this._releaseCaller();
      return;
    }

    this._onIdle = hooks.onIdle ?? null;
    this._onClosed = hooks.onClosed ?? null;

    const clip = this._clipName();
    if (!clip) {
      console.warn("[AdditionalFreeSpins] No clip to play for level:", this.level);
      // Same rule as the guard above — the caller is waiting on this.
      this._releaseCaller();
      return;
    }

    const timeline = this._retriggerTimeline(clip);
    this.isPlaying = true;
    this._fired = { boxShut: false, inEnd: false, outStart: false, done: false };
    this._clearTimeouts();

    this.overlayRef?.current?.fadeInBackground();
    // Blocks the play button while a modal is up. FreeSpinRetriggerController
    // raised this and the rename dropped it, so every guard that reads it
    // stopped firing for this popup.
    this.dispatch?.(setCriticalAnimationPlaying(true));
    this.applyScaleAndCenter();

    this.spine.state.clearTracks();
    this.spine.state.clearListeners();

    // ── The clip drives everything ──
    //
    // Three events inside it and its own `complete` are the whole sequence.
    // No phase is started by code, so nothing can start one late or out of
    // order — see the EVENT_* constants.
    this.spine.state.addListener({
      event: (_entry, event) => {
        if (!this.isPlaying) return;
        switch (event?.data?.name) {
          case EVENT_BOX_SHUT:
            this._beatBoxShut();
            break;
          case EVENT_IN_END:
            this._beatInEnd();
            break;
          case EVENT_OUT_START:
            this._beatOutStart();
            break;
          default:
            break;
        }
      },
      complete: (entry) => {
        // if (entry?.animation?.name === clip && this.isPlaying) {
        if (entry?.animation?.name === timeline.last && this.isPlaying) {
          // Whatever the clip failed to announce is announced now. A popup
          // that ends without having released the counter leaves the button
          // reading low for the rest of the round.
          this._beatInEnd();
          this.finishAnimation();
        }
      },
    });

    // ── Back to the SETUP POSE first, and this is load-bearing ──
    //
    // `clearTracks()` above removes the animation; it does NOT undo what that
    // animation posed. The skeleton keeps the last frame it was left on, and
    // one rig now serves all four popups, so whatever the previous clip posed
    // and the next one never mentions stays exactly where it was parked.
    //
    // Measured in the export, not guessed:
    //
    //   lvl_x10          moves bone `x10_on3`      — additional_spins does not
    //   lvl_x3           moves bone `x3_on`        — additional_spins does not
    //   lvl_x5           moves bone `x5_on2`       — additional_spins does not
    //   additional_spins moves bone `txt_slot_freespins` — no lvl_ clip does
    //
    // and the same asymmetry runs through their slots (`bonus_5_on`,
    // `bonus_x_2_on3`, `mystery_door_l4/r4`, the glows). So a level-up popup
    // followed by a "+N free spins" popup left the big `x10` lit and parked
    // over the new headline — two announcements on screen at once.
    //
    // The old rig could not do this: each level had its own in/idle/out chain,
    // so a clip only ever followed its own siblings. The single automatic clip
    // per case is what made a reset necessary, and nothing added one.
    //
    // Safe here, and only here: the popup is still HIDDEN on this frame (alpha
    // is raised in the RAF below), so the reset can never be seen — the flash
    // risk that makes setToSetupPose dangerous mid-animation does not exist at
    // this point. The injected label survives it: spine-pixi only ties a slot
    // object's visibility to the attachment when `followAttachmentTimeline` is
    // set, and this controller does not set it.
    this.spine.skeleton.setToSetupPose();

    // Played while STILL HIDDEN, then shown on the NEXT frame — the warm-up
    // pose is gone by then and the clip starts fresh.
    if (this.spine) this.spine.autoUpdate = true;
    this.spine.state.setAnimation(0, clip, false);
    // The new spine: in -> idle -> out, queued (the card closes itself).
    timeline.queue.forEach((name) => this.spine.state.addAnimation(0, name, false, 0));
    // The popup arriving. On the clip's own frame 0, not on the frame it is
    // made visible — those are one apart and the sound has an attack.
    soundManager.play(SOUND_NAME.additional_free_spins_intro);
    if (this._showRAF) cancelAnimationFrame(this._showRAF);
    this._showRAF = requestAnimationFrame(() => {
      this._showRAF = null;
      if (this.isPlaying && this.spine) this.spine.alpha = 1;
    });

    // ── And the same beats on the ticker, as a net ──
    //
    // Armed only so a re-export that drops the hand-keyed events cannot leave
    // the round waiting. Each beat is idempotent, so whichever source arrives
    // first is the one that counts. See FALLBACK_BEATS.
    // const total = this.getSpineAnimationDuration(clip);
    const { total } = timeline;
    this.inWinTimeout = new TickerTimeout(
      () => this._beatBoxShut(),
      // total * FALLBACK_BEATS.boxShut,
      timeline.boxShut,
    );
    this.inIdleTimeout = new TickerTimeout(
      () => this._beatInEnd(),
      // total * FALLBACK_BEATS.inEnd,
      timeline.inEnd,
    );
    this.outStartTimeout = new TickerTimeout(
      () => this._beatOutStart(),
      // total * FALLBACK_BEATS.outStart,
      timeline.outStart,
    );
    // One frame past the end, so the clip's own `complete` always wins.
    this.completeTimeout = new TickerTimeout(() => {
      if (!this.isPlaying || this._fired.done) return;
      this._beatInEnd();
      this.finishAnimation();
    }, total + 32);
  }

  /**
   * When each beat falls, ms. The new spine: in, idle x RETRIGGER_IDLE_LOOPS,
   * out — the count lands mid-`in`, the counter updates at the end of `in`
   * (the card is up), the out sound with `out`. The Mad Labs one-shot keeps
   * its fractions (FALLBACK_BEATS).
   */
  _retriggerTimeline(clip) {
    const available = this.getAvailableAnimations();
    if (clip === "in" && available.includes("idle") && available.includes("out")) {
      const inMs = this.getSpineAnimationDuration("in");
      const idleMs = this.getSpineAnimationDuration("idle") * RETRIGGER_IDLE_LOOPS;
      const outMs = this.getSpineAnimationDuration("out");
      return {
        queue: [...Array(RETRIGGER_IDLE_LOOPS).fill("idle"), "out"],
        last: "out",
        boxShut: inMs * 0.5,
        inEnd: inMs,
        outStart: inMs + idleMs,
        total: inMs + idleMs + outMs,
      };
    }
    const total = this.getSpineAnimationDuration(clip);
    return {
      queue: [],
      last: clip,
      boxShut: total * FALLBACK_BEATS.boxShut,
      inEnd: total * FALLBACK_BEATS.inEnd,
      outStart: total * FALLBACK_BEATS.outStart,
      total,
    };
  }

  // ── Beats ────────────────────────────────────────────────────────────────
  //
  // One method each, and each one may run only once per play. They are called
  // both by the spine event and by its fallback timer, and the flag is what
  // makes the pair safe.

  /** The winning box pops and the count is shown. */
  _beatBoxShut() {
    if (!this.isPlaying || this._fired.boxShut) return;
    this._fired.boxShut = true;
    soundManager.play(SOUND_NAME.additional_free_spins_win);
  }

  /**
   * The card is up and the screen is covered.
   *
   * Everything the player is being TOLD moves here: the room colour, the spin
   * counter. See the hooks the level-charge sequence passes in.
   */
  _beatInEnd() {
    if (!this.isPlaying || this._fired.inEnd) return;
    this._fired.inEnd = true;
    const onIdle = this._onIdle;
    this._onIdle = null;
    onIdle?.();
  }

  /** The card starts to fade. */
  _beatOutStart() {
    if (!this.isPlaying || this._fired.outStart) return;
    this._fired.outStart = true;
    soundManager.play(SOUND_NAME.additional_free_spins_outro);
  }

  /**
   * End the caller's wait without having shown anything.
   *
   * Only for the paths that never start the clip. Everything else ends at
   * finishAnimation, which fires onClosed behind the overlay fade.
   *
   * `criticalAnimationPlaying` is deliberately NOT lowered here. It has not
   * been raised yet on this path, and the level-charge sequence around us is
   * holding its own — clearing it would unlock the play button while that
   * sequence still owns the screen.
   *
   * ── The flag MUST come down, and this is why ──
   *
   * `showAdditionalFreeSpins` is raised by the CALLER, one line before
   * startSequence, so every path that declines to show a popup leaves it up.
   * That used to be harmless: the overlay was invisible to the pointer and
   * nothing listened for a key. It is not harmless any more. The popup was
   * made modal on 2026-09-21 — a screen-sized absorber and a capture-phase
   * keydown swallow, both gated on that flag — so a flag stuck up is a
   * transparent rectangle eating every tap and a listener eating the spacebar,
   * over a board that looks perfectly normal.
   *
   * That is the "+7 got stuck" from demo 2. The bug is not the popup failing
   * to open; it is the game being locked by a popup that never opened.
   */
  _releaseCaller() {
    this.isPlaying = false;
    this._onIdle = null;
    this.dispatch?.(setShowAdditionalFreeSpins(false));
    const closed = this._onClosed;
    this._onClosed = null;
    closed?.();
  }

  finishAnimation() {
    if (!this.spine || !this.initialized) {
      // There is nothing left to animate out, but the round is waiting on this
      // popup and the flag is up. Both have to end here, or the screen stays
      // locked behind an absorber with no popup on it.
      this.dispatch?.(setCriticalAnimationPlaying(false));
      this._releaseCaller();
      return;
    }

    this.isPlaying = false;
    // Closes the door on the fallback timer AND on a second `complete`, either
    // of which would otherwise fade the overlay out a second time.
    this._fired.done = true;
    this.spine.alpha = 0;
    if (this.spine) this.spine.autoUpdate = false;

    if (this._showRAF) {
      cancelAnimationFrame(this._showRAF);
      this._showRAF = null;
    }
    this._clearTimeouts();
    this.dispatch?.(setCriticalAnimationPlaying(false));

    // Overlay fades LAST, and the flag clears with it — that ordering is what
    // gives `out` its frames instead of unmounting on top of it.
    this.overlayRef?.current?.fadeOutBackground(() => {
      this.dispatch?.(setShowAdditionalFreeSpins(false));

      // LAST, after the overlay has gone: whatever resumes here runs on a
      // clear screen. Nulled before the call so a handler that reopens the
      // popup cannot be fired twice by its own reopen.
      const onClosed = this._onClosed;
      this._onClosed = null;
      onClosed?.();
    });
  }

  cleanup() {
    // Anything waiting on this popup is released before it goes.
    //
    // The level-up sequence awaits onClosed while holding
    // criticalAnimationPlaying. A popup torn down mid-sequence — a resize
    // rebuild, a scene swap — would never fire it, and the round would sit
    // there with autoplay locked and nothing left alive to unlock it.
    const onClosed = this._onClosed;
    this._onIdle = null;
    this._onClosed = null;
    // Torn down with the flag still up leaves the same locked screen — the
    // component survives the controller, and its absorber reads the flag.
    this.dispatch?.(setShowAdditionalFreeSpins(false));
    onClosed?.();

    if (this._showRAF) {
      cancelAnimationFrame(this._showRAF);
      this._showRAF = null;
    }
    this._clearTimeouts();

    if (this.spine) {
      this.spine.state?.clearListeners();
      this.spine.state?.clearTracks();
      if (this.spine.removeSlotObjects) this.spine.removeSlotObjects();
      if (this.container?.children.includes(this.spine)) {
        this.container.removeChild(this.spine);
      }
      this.spine.destroy();
      this.spine = null;
    }

    // Destroyed with the spine's children above, so only the refs are dropped.
    this.labelContainer = null;
    this.labelText = null;
    this.topText = null;
    this.numberText = null;
    this.initialized = false;
  }
}

export default AdditionalFreeSpinsController;
